import type { ReactNode } from 'react';
import {
  BedIcon, BellIcon, BoltIcon, CameraIcon, ChartIcon, CheckIcon, DotIcon, EllipsisVerticalIcon, GearIcon, MedalIcon, PencilIcon,
  PlayIcon, PrinterIcon, ScaleIcon, StopIcon, SwapIcon, TrashIcon, WarningIcon, XMarkIcon,
} from './Icons';

/**
 * #603 — one icon style: only a flag is an emoji. A text that names an icon
 * holds the emoji as a token: a step of the Manual, a notice in the bell.
 * `renderWithIcons()` draws the SVG of the app in its place. A push
 * notification cannot draw an SVG, so a push text names the thing in words.
 * `icons-svg-603.spec.ts` keeps every other emoji out of frontend/src.
 */
const INLINE_CLASS = 'inline-block align-text-bottom w-3.5 h-3.5 mx-0.5';

export const INLINE_ICON: Readonly<Record<string, ReactNode>> = {
  '✎': <PencilIcon className={INLINE_CLASS} />,
  '📷': <CameraIcon className={INLINE_CLASS} />,
  '✕': <XMarkIcon className={INLINE_CLASS} />,
  '✓': <CheckIcon className={INLINE_CLASS} />,
  '🔔': <BellIcon className={INLINE_CLASS} />,
  '🖨': <PrinterIcon className={INLINE_CLASS} />,
  '🗑': <TrashIcon className={INLINE_CLASS} />,
  '⚡': <BoltIcon className={INLINE_CLASS} />,
  '🛏': <BedIcon className={INLINE_CLASS} />,
  '📊': <ChartIcon className={INLINE_CLASS} />,
  '🥇': <MedalIcon className={INLINE_CLASS} place={1} />,
  '🥈': <MedalIcon className={INLINE_CLASS} place={2} />,
  '🥉': <MedalIcon className={INLINE_CLASS} place={3} />,
  '⚙': <GearIcon className={INLINE_CLASS} />,
  '👨‍⚖️': <ScaleIcon className={INLINE_CLASS} />,
  '▶': <PlayIcon className={INLINE_CLASS} />,
  // The Stop of the Sarama music: an icon with no text on the button (#600).
  '⏹': <StopIcon className={`${INLINE_CLASS} text-red-600`} />,
  '⚠': <WarningIcon className={INLINE_CLASS} />,
  '↔': <SwapIcon className={INLINE_CLASS} />,
  // The menu of Chrome on Android, in the install steps of the Manual.
  '⋮': <EllipsisVerticalIcon className={INLINE_CLASS} />,
  // The corner of a boxer in a notice of the bout list (services/boutEvents.ts).
  '🔴': <DotIcon className={`${INLINE_CLASS} text-red-600`} />,
  '🔵': <DotIcon className={`${INLINE_CLASS} text-blue-600`} />,
};

/** The tokens of the two corners, so another file names them with no emoji. */
export const CORNER_TOKEN = { red: '🔴', blue: '🔵' } as const;

// The longest token first: '👨‍⚖️' holds more than one code point.
const ICON_REGEX = new RegExp(
  Object.keys(INLINE_ICON)
    .sort((a, b) => b.length - a.length)
    .map(g => g.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'gu',
);

/** The text with the SVG of the app in place of each icon token. `names`
 *  gives a token a spoken name: the emoji had one, and the SVG has none. */
export function renderWithIcons(text: string, names: Readonly<Record<string, string>> = {}): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  ICON_REGEX.lastIndex = 0;
  while ((m = ICON_REGEX.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const name = names[m[0]];
    out.push(name
      ? <span key={key++} role="img" aria-label={name}>{INLINE_ICON[m[0]]}</span>
      : <span key={key++}>{INLINE_ICON[m[0]]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
